import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { api } from "@/lib/api-client";
import { toast } from "sonner";
import type { NotificationSettings } from "@/hooks/useNotificationSettings";

export type IntegrationStatus = "connected" | "not_connected" | "error" | "connecting";

export interface IntegrationInfo {
  key: string;
  status: IntegrationStatus;
  metadata?: string;     // e.g. "Connected to #general · Tested 2m ago"
  errorMessage?: string;
}

/**
 * Derives integration status from real notification_settings + profile data.
 * No mocks. Green only if credentials actually exist in the DB.
 */
export function useIntegrationStatuses() {
  const { data: settings, isLoading: settingsLoading } = useQuery<NotificationSettings>({
    queryKey: ["notification_settings"],
    queryFn: () => api.getSettings(),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => api.getProfile(),
    staleTime: 30_000,
  });

  const isLoading = settingsLoading || profileLoading;

  const statuses: Record<string, IntegrationInfo> = {
    slack: {
      key: "slack",
      status: settings?.slack_oauth_access_token
        ? "connected"
        : settings?.slack_webhook_url
        ? "connected"
        : "not_connected",
      metadata: settings?.slack_workspace_name
        ? `${settings.slack_workspace_name}${settings.slack_channel_name ? ` · #${settings.slack_channel_name}` : ""}`
        : settings?.slack_webhook_url
        ? "Webhook connected"
        : undefined,
    },
    telegram: {
      key: "telegram",
      status: settings?.telegram_enabled && settings?.telegram_bot_token ? "connected" : "not_connected",
      metadata: settings?.telegram_enabled ? `Bot @${import.meta.env.VITE_TELEGRAM_BOT_USERNAME || "UptimewingBot"} active` : undefined,
    },
    discord: {
      key: "discord",
      status: settings?.discord_enabled && settings?.discord_webhook_url ? "connected" : "not_connected",
      metadata: settings?.discord_enabled ? "Webhook connected" : undefined,
    },
    email: {
      key: "email",
      status: settings?.email_enabled && settings?.email_to_address ? "connected" : "not_connected",
      metadata: settings?.email_to_address || undefined,
    },
    teams: {
      key: "teams",
      status: settings?.teams_enabled && settings?.teams_webhook_url ? "connected" : "not_connected",
      metadata: settings?.teams_enabled ? "Webhook connected" : undefined,
    },
    github: {
      key: "github",
      status: profile?.github_access_token ? "connected" : "not_connected",
      metadata: profile?.github_access_token ? `${profile?.github_repos_count ?? ""} repos accessible`.trim() : undefined,
    },
    vercel: {
      key: "vercel",
      status: settings?.vercel_enabled && settings?.vercel_webhook_url ? "connected" : "not_connected",
      metadata: settings?.vercel_project ? `Project: ${settings.vercel_project}` : undefined,
    },
    linear: {
      key: "linear",
      status: settings?.linear_enabled && settings?.linear_api_key ? "connected" : "not_connected",
      metadata: settings?.linear_project_key ? `Project: ${settings.linear_project_key}` : undefined,
    },
    jira: {
      key: "jira",
      status: settings?.jira_enabled && settings?.jira_api_key ? "connected" : "not_connected",
      metadata: settings?.jira_url ? settings.jira_url : undefined,
    },
    datadog: {
      key: "datadog",
      status: settings?.datadog_enabled && settings?.datadog_api_key ? "connected" : "not_connected",
      metadata: settings?.datadog_enabled ? "Metrics connected" : undefined,
    },
    grafana: {
      key: "grafana",
      status: settings?.grafana_enabled && settings?.grafana_api_key ? "connected" : "not_connected",
      metadata: settings?.grafana_url || undefined,
    },
    webhooks: {
      key: "webhooks",
      status: "not_connected", // managed separately via useWebhooks
    },
    pagerduty: {
      key: "pagerduty",
      status: "not_connected", // OAuth not yet configured server-side
    },
  };

  const connectedCount = Object.values(statuses).filter(s => s.status === "connected").length;

  return { statuses, settings, profile, isLoading, connectedCount };
}

/**
 * Hook to test a specific integration via the test-integration edge function.
 */
export function useTestIntegration() {
  return useMutation({
    mutationFn: async ({ type, settings, profile }: { type: string; settings: any; profile: any }) => {
      const signal = AbortSignal.timeout(15_000);

      if (type === "slack") {
        const { data, error } = await supabase.functions.invoke("test-integration", {
          body: { type: "slack", url: settings?.slack_webhook_url },
        });
        if (error || data?.error) throw new Error(error?.message || data?.error || "Slack test failed");
        return data;
      }

      if (type === "telegram") {
        const { data, error } = await supabase.functions.invoke("test-integration", {
          body: { type: "telegram", botToken: settings?.telegram_bot_token, chatId: settings?.telegram_chat_id },
        });
        if (error || data?.error) throw new Error(error?.message || data?.error || "Telegram test failed");
        return data;
      }

      if (type === "discord") {
        const { data, error } = await supabase.functions.invoke("test-integration", {
          body: { type: "discord", url: settings?.discord_webhook_url },
        });
        if (error || data?.error) throw new Error(error?.message || data?.error || "Discord test failed");
        return data;
      }

      if (type === "teams") {
        if (!settings?.teams_webhook_url) throw new Error("No Teams webhook URL configured");
        const res = await fetch(settings.teams_webhook_url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            "@type": "MessageCard",
            "@context": "http://schema.org/extensions",
            summary: "UptimeWing Test",
            themeColor: "0076D7",
            title: "✅ UptimeWing Test",
            text: "Your Microsoft Teams integration is working correctly.",
          }),
          signal,
        });
        if (!res.ok) throw new Error(`Teams returned ${res.status}`);
        return { success: true };
      }

      if (type === "github") {
        if (!profile?.github_access_token) throw new Error("GitHub not connected");
        const res = await fetch("https://api.github.com/user", {
          headers: { Authorization: `token ${profile.github_access_token}` },
          signal,
        });
        if (!res.ok) throw new Error("GitHub token invalid or expired");
        const user = await res.json();
        return { success: true, username: user.login };
      }

      if (type === "vercel") {
        if (!settings?.vercel_webhook_url) throw new Error("No Vercel webhook configured");
        const res = await fetch(settings.vercel_webhook_url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ test: true, source: "uptimewing" }),
          signal,
        });
        if (!res.ok && res.status !== 404) throw new Error(`Vercel returned ${res.status}`);
        return { success: true };
      }

      if (type === "linear") {
        if (!settings?.linear_api_key) throw new Error("No Linear API key configured");
        const res = await fetch("https://api.linear.app/graphql", {
          method: "POST",
          headers: { Authorization: settings.linear_api_key, "Content-Type": "application/json" },
          body: JSON.stringify({ query: "{ viewer { id name } }" }),
          signal,
        });
        const json = await res.json();
        if (json.errors) throw new Error(json.errors[0]?.message || "Linear API error");
        return { success: true, username: json.data?.viewer?.name };
      }

      if (type === "jira") {
        if (!settings?.jira_api_key || !settings?.jira_url) throw new Error("Jira not configured");
        const [email, token] = settings.jira_api_key.split(":");
        const basic = btoa(`${email}:${token}`);
        const res = await fetch(`${settings.jira_url}/rest/api/3/myself`, {
          headers: { Authorization: `Basic ${basic}`, Accept: "application/json" },
          signal,
        });
        if (!res.ok) throw new Error(`Jira returned ${res.status}`);
        const user = await res.json();
        return { success: true, username: user.displayName };
      }

      if (type === "datadog") {
        if (!settings?.datadog_api_key) throw new Error("No Datadog API key configured");
        const res = await fetch("https://api.datadoghq.com/api/v1/validate", {
          headers: { "DD-API-KEY": settings.datadog_api_key },
          signal,
        });
        if (!res.ok) throw new Error(`Datadog returned ${res.status}`);
        return { success: true };
      }

      if (type === "grafana") {
        if (!settings?.grafana_api_key || !settings?.grafana_url) throw new Error("Grafana not configured");
        const res = await fetch(`${settings.grafana_url}/api/org`, {
          headers: { Authorization: `Bearer ${settings.grafana_api_key}` },
          signal,
        });
        if (!res.ok) throw new Error(`Grafana returned ${res.status}`);
        return { success: true };
      }

      throw new Error(`Unknown integration type: ${type}`);
    },
    onSuccess: (data, variables) => {
      const username = (data as any)?.username;
      toast.success(
        username
          ? `✓ ${variables.type} connected as ${username}`
          : `✓ ${variables.type} test successful — integration is working!`
      );
    },
    onError: (err: Error, variables) => {
      toast.error(`${variables.type} test failed: ${err.message}`);
    },
  });
}

/**
 * Disconnect an integration by clearing its credentials from notification_settings.
 */
export function useDisconnectIntegration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ type, onSave, onProfileUpdate }: {
      type: string;
      onSave?: (updates: any) => Promise<void>;
      onProfileUpdate?: (updates: any) => Promise<void>;
    }) => {
      const clearMap: Record<string, any> = {
        slack: { slack_webhook_url: null, slack_enabled: false, slack_oauth_access_token: null, slack_workspace_name: null, slack_channel_id: null, slack_channel_name: null },
        telegram: { telegram_bot_token: null, telegram_chat_id: null, telegram_enabled: false },
        discord: { discord_webhook_url: null, discord_enabled: false },
        email: { email_to_address: null, email_enabled: false },
        teams: { teams_webhook_url: null, teams_enabled: false },
        vercel: { vercel_webhook_url: null, vercel_enabled: false, vercel_project: null },
        linear: { linear_api_key: null, linear_project_key: null, linear_enabled: false },
        jira: { jira_api_key: null, jira_project_key: null, jira_url: null, jira_enabled: false },
        datadog: { datadog_api_key: null, datadog_application_key: null, datadog_enabled: false },
        grafana: { grafana_api_key: null, grafana_url: null, grafana_enabled: false },
      };

      if (type === "github") {
        if (onProfileUpdate) {
          await onProfileUpdate({ github_access_token: null, github_refresh_token: null, github_token_expires_at: null });
        }
        return;
      }

      const updates = clearMap[type];
      if (updates && onSave) {
        await onSave(updates);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["notification_settings"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success(`${variables.type} disconnected successfully.`);
    },
    onError: (err: Error) => {
      toast.error(`Failed to disconnect: ${err.message}`);
    },
  });
}
